import React, { useState, useEffect, useRef } from 'react';
import DataTable from './DataTable';
import { getMappings, deleteMapping } from '../../lib/adminApi';

function AdminMappings() {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Ref to prevent double API calls in StrictMode
  const hasFetched = useRef(false);

  const fetchMappings = async (page = 1) => {
    setLoading(true);
    try {
      const data = await getMappings(page, pagination.limit);
      setMappings(data.mappings || []);
      setPagination({ page: data.page, limit: data.limit, total: data.total });
    } catch (err) {
      console.error('Failed to fetch mappings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prevent double fetch in StrictMode
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchMappings();
  }, []);

  const handleDelete = async (userId, questionId) => {
    try {
      await deleteMapping(userId, questionId);
      setDeleteConfirm(null);
      fetchMappings(pagination.page);
    } catch (err) {
      console.error('Failed to delete mapping:', err);
    }
  };

  const columns = [
    { key: 'userId', label: 'User ID' },
    { key: 'email', label: 'Email' },
    { key: 'questionId', label: 'Question ID' },
    { key: 'noOfAttempts', label: 'Attempts' },
    {
      key: 'solved',
      label: 'Solved',
      render: (val) => (
        <span className={`px-2 py-1 rounded text-xs ${val ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
          {val ? 'Yes' : 'No'}
        </span>
      ),
    },
  ];

  const actions = (row) => (
    <div className="flex gap-2 justify-end">
      <button
        onClick={() => setDeleteConfirm(row)}
        className="px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-700"
        title="Reset user's progress on this question"
      >
        Reset
      </button>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">User Progress</h1>
      </div>

      <DataTable
        columns={columns}
        data={mappings}
        loading={loading}
        pagination={pagination}
        onPageChange={fetchMappings}
        actions={actions}
        emptyMessage="No progress data found."
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Reset Progress</h2>
            <p className="text-gray-300 mb-6">
              Reset progress for user {deleteConfirm.email} on question #{deleteConfirm.questionId}?
              This will remove their attempt count and solved status.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.userId, deleteConfirm.questionId)}
                className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminMappings;
